import { SessionContextData } from '../../../../../common/Session/SessionContext';
import { getPresignedUrls, uploadWithPresignedUrl } from '../../../../../common/upload';
import { updateSubmission } from '../../UserDeveloper/Submission/Mutations/update-submission-mutation';
import { updateSubmissionAddQAResultAndNote } from '../../UserDeveloper/Submission/Mutations/update-submission-qa-result-mutation';
import { submissionSubmitForApproval } from '../../UserDeveloper/Submission/Mutations/submission-submit-for-approval';
import { developerSubmissionUpdateLegalForm } from '../../UserDeveloper/Submission/Mutations/submission-update-legal-form';
import { submitGame } from '../../UserDeveloper/SubmitGame/submit-game-graphql';
import { ENABLE_TEST_UTILITIES } from './TestUtilities';
import { createDefaultBasicRequirements, createDefaultFullRequirements } from '../../UserDeveloper/QATool/helpers/qa-result-helpers';
import { mutate } from '../../../../../common/graphql';
import {
  ADMIN_SUBMISSION_GENERATE_FAKE_LEADERBOARD_DATA_MUTATION,
  AdminSubmissionGenerateFakeLeaderboardDataMutation,
  AdminSubmissionGenerateFakeLeaderboardDataInput,
} from '@crazygames/graph-types/submission/admin-submission-release-with-leaderboard-input.type';
import { UploadFile } from '../../../../../common/domain/upload';
import { GameBuild } from '../Game/gql/game-build-graphql';

interface CreateSubmissionReturnType {
  id: string;
  slug: string;
  gameBuilds: GameBuild[];
}

/** Create draft submission with engine and files, that didn't pass QA yet. */
export async function _testUtilityCreatePartialDraftSubmission(
  name: string,
  session: SessionContextData,
): Promise<CreateSubmissionReturnType> {
  if (!ENABLE_TEST_UTILITIES) {
    throw new Error('Test utilities are disabled');
  }

  const jsResponse = await fetch('https://html5-sdk-test-v3.game-files.crazygames.com/html5-sdk-test-v3/5/bundle.js');
  const jsContent = await jsResponse.text();

  const presignedResponses = await getPresignedUrls(
    [
      {
        path: 'index.html',
        blob: { name: 'index.html' } as File,
      } as UploadFile,
      {
        path: 'bundle.js',
        blob: { name: 'bundle.js' } as File,
      } as UploadFile,
    ],
    session,
  );

  const jsBlob = new File([jsContent], 'bundle.js', { type: 'application/javascript' });

  const htmlFileToUpload: UploadFile = {
    state: 'WAITING',
    path: 'index.html',
    size: 0,
    imageUrl: null,
    blob: new File(
      [
        `<!doctype html><html lang="en"><head><meta charset="UTF-8"/><title>CrazyGames - HTML5 SDK Test Game</title><script src="./bundle.js"></script><style>body {
        font-size: 1rem;
        font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;
        font-weight: 400;
        line-height: 1.5;
        letter-spacing: 0.00938em;
      }</style><script defer="defer" src="bundle.js"></script></head><body></body></html>`,
      ],
      'index.html',
      { type: 'text/html' },
    ),
    presigned: {
      url: presignedResponses[0].presignedUrl,
      encoding: presignedResponses[0].encoding,
      type: presignedResponses[0].type,
    },
  };

  // TODO: couldn't get this to work and upload custom game files, so it's half-baked for now (also gameCovers would need some work)
  const jsFileToUpload: UploadFile = {
    state: 'WAITING',
    path: 'bundle.js',
    size: jsBlob.size,
    imageUrl: null,
    blob: jsBlob,
    presigned: {
      url: presignedResponses[1].presignedUrl,
      encoding: presignedResponses[1].encoding,
      type: presignedResponses[1].type,
    },
  };

  const uploadedHtmlFile = await uploadWithPresignedUrl(htmlFileToUpload, session);
  const uploadedJsFile = await uploadWithPresignedUrl(jsFileToUpload, session);

  const newSubmission = await submitGame({
    apsDetail: { progressType: 'SDKPS' },
    fullscreen: 'NOT_SUPPORTED',
    gameLoaderType: 'html5',
    gameName: name,
    hasIAP: false,
    isAndroidFriendly: false,
    isChromebookFriendly: false,
    isIOSFriendly: false,
    orientation: null,
    gameFiles: [
      {
        path: uploadedHtmlFile.fileName,
        size: uploadedHtmlFile.size,
        uploadId: uploadedHtmlFile.uploadId,
      },
      {
        path: uploadedJsFile.fileName,
        size: uploadedJsFile.size,
        uploadId: uploadedJsFile.uploadId,
      },
    ],
  });

  if (!newSubmission.data?.developerSubmissionCreate) {
    throw new Error('Failed to create draft submission');
  }

  return newSubmission.data?.developerSubmissionCreate;
}

/** Create draft submission with engine, files, tag, categories, thumbnails, description etc. It needs the legal form to be completed now. */
export async function _testUtilityCreateFullDraftSubmission(
  name: string,
  session: SessionContextData,
  hasCovers: boolean = true,
): Promise<CreateSubmissionReturnType> {
  const submission = await _testUtilityCreatePartialDraftSubmission(name, session);

  await updateSubmissionAddQAResultAndNote({
    id: submission.id,
    gameBuildId: submission.gameBuilds[0].id,
    qaResult: {
      detectedSDKFunctionality: [],
      basicRequirements: createDefaultBasicRequirements(),
      fullRequirements: createDefaultFullRequirements(),
      initialLoadSizeBytes: 1,
      SDKImplemented: false,
      adScenario: '',
    },
    updateNoteText: '',
  });

  await developerSubmissionUpdateLegalForm({
    id: submission.id,
    revenueShareInputV3: {
      isEligible: true,
      makeExclusive: false,
      hasImplementedSDK: false,
      suitableForChildren: false,
      hasCustomContract: false,
    },
  });

  const files = submission.gameBuilds[0]?.files || [];

  await updateSubmission({
    id: submission.id,
    gameName: name,
    hasIAP: false,
    isChromebookFriendly: false,
    gameLoaderType: 'html5',
    fullscreen: 'NOT_SUPPORTED',
    isIOSFriendly: false,
    isAndroidFriendly: false,
    gameFiles: files.map((file) => ({
      path: file.fileName,
      size: file.size,
      uploadId: file.uploadFileId,
    })),
    apsDetail: {
      progressType: 'SDKPS',
    },
    description: 'Description',
    controls: 'Controls',
    playStoreLink: undefined,
    appStoreLink: undefined,
    steamStoreLink: undefined,
    categoryId: '0376053f-8b1e-4ee9-8d3d-cb49a5f21d89',
    tagIds: ['338ace27-8fd2-438a-b9b8-de452ab0a3d8'],
    // these will be missing as you won't have the uploaded files in your DB locally
    gameCovers: hasCovers
      ? [
          {
            size: '16x9',
            uploadedFile: {
              path: '_background2_1920x1080.png',
              uploadId: '0daf0e31-15bf-4f12-898f-092d484e60e8',
              size: 4517961,
            },
          },
          {
            size: '2x3',
            uploadedFile: {
              path: '_background2_800x1200.png',
              uploadId: '28831a06-af81-41a0-8b57-530ddc3c6dc6',
              size: 2140721,
            },
          },
          {
            size: '1x1',
            uploadedFile: {
              path: '_background2_800x800.png',
              uploadId: 'cd58b776-76b3-4384-8736-9fcc69b0c1fc',
              size: 3177343,
            },
          },
        ]
      : undefined,
    updateStep: 'fullEdit',
    videos: null,
    orientation: null,
    allowEmbed: false,
  });

  return submission;
}

/** Create a full submission that passed QA tool, has the legal form completed and is submitted. Requires approval and release now. */
export async function _testUtilityCreateSubmittedSubmission(
  name: string,
  session: SessionContextData,
  hasCovers: boolean = true,
): Promise<CreateSubmissionReturnType> {
  const submission = await _testUtilityCreateFullDraftSubmission(name, session, hasCovers);

  await submissionSubmitForApproval({
    id: submission.id,
  });

  return submission;
}

interface CreateLeaderboardDataReturnType {
  submissionId: string;
  gameId: string;
  slug: string;
}

/**
 * Create released submission with leaderboard test data
 */
export async function _testUtilityCreateLeaderboardData(
  name: string,
  session: SessionContextData,
): Promise<CreateLeaderboardDataReturnType> {
  if (!ENABLE_TEST_UTILITIES) {
    throw new Error('Test utilities are disabled');
  }

  // create a submitted submission
  const submission = await _testUtilityCreateSubmittedSubmission(name, session, false);

  // release the submission and setup leaderboard
  return await _testUtilityReleaseSubmissionWithLeaderboard(submission.id);
}

/**
 * Takes an existing submitted submission and releases it with leaderboard configuration
 */
async function _testUtilityReleaseSubmissionWithLeaderboard(submissionId: string): Promise<CreateLeaderboardDataReturnType> {
  const result = await mutate<AdminSubmissionGenerateFakeLeaderboardDataInput, AdminSubmissionGenerateFakeLeaderboardDataMutation>(
    ADMIN_SUBMISSION_GENERATE_FAKE_LEADERBOARD_DATA_MUTATION,
    { id: submissionId },
  );

  const submission = result.data?.adminSubmissionGenerateFakeLeaderboardData;

  if (!submission || !submission.gameId || !submission.slug) {
    throw new Error('Failed to release submission with leaderboard');
  }

  return {
    submissionId: submission.id,
    gameId: submission.gameId,
    slug: submission.slug,
  };
}
